import AdsClick from '@mui/icons-material/AdsClick';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import {
    Box,
    IconButton,
    Slider,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * CallTracking component for the pricing calculator
 * @param {Object} props - Component props
 * @param {Object} props.product - Product data (value, min, max, etc.)
 * @param {Function} props.toggleProduct - Function to toggle product selection
 * @param {boolean} props.hasCustomSettings - Whether custom settings are applied
 * @param {Function} props.openSettings - Function to open settings modal
 * @param {string} props.callTrackingType - Type of call tracking ('dynamic', 'static', 'combined')
 * @param {number} props.staticSources - Number of static sources
 * @param {Function} props.setStaticSources - Function to set static sources
 * @param {Object} props.phoneNumbersNeeded - Object with phone numbers needed info
 */
const CallTracking = ({ 
  product, 
  toggleProduct, 
  hasCustomSettings, 
  openSettings, 
  callTrackingType, 
  staticSources, 
  setStaticSources,
  phoneNumbersNeeded
}) => {
  const { t } = useTranslation();

  // Local state for the TextField to allow temporary empty string
  const [sessionsInput, setSessionsInput] = React.useState(product.value);
  const [sourcesInput, setSourcesInput] = React.useState(staticSources);

  // Sync local state if the prop value changes from elsewhere
  React.useEffect(() => {
    if (product.value !== sessionsInput) {
      setSessionsInput(product.value);
    }
  }, [product.value]);

  React.useEffect(() => {
    if (staticSources !== sourcesInput) {
      setSourcesInput(staticSources);
    }
  }, [staticSources]);

  const handleSessionsBlur = () => {
    let numericValue = parseInt(sessionsInput, 10);
    if (isNaN(numericValue) || numericValue < product.min) {
      numericValue = product.min;
    }
    if (numericValue > product.max) {
      numericValue = product.max;
    }
    setSessionsInput(numericValue);
    product.setValue(numericValue);
  };

  const handleSourcesBlur = () => {
    let numericValue = parseInt(sourcesInput, 10);
    if (isNaN(numericValue) || numericValue < 0) {
      numericValue = 0;
    }
    if (numericValue > 50) {
      numericValue = 50;
    }
    setSourcesInput(numericValue);
    setStaticSources(numericValue);
  };


  return (
    <Box 
      sx={{ 
        borderRadius: 2
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AdsClick sx={{ color: '#333', fontSize: 20, mr: 1 }} />
          <Typography sx={{ fontWeight: 500 }}>
            {product.label}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={t('calculator.callTrackingSettings', 'Call Tracking Settings')}>
            <IconButton 
              size="small" 
              onClick={openSettings}
              sx={{ color: hasCustomSettings ? '#00C853' : '#757575', mr: 1, '&:hover': { color: '#00C853' } }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton 
            size="small" 
            onClick={() => toggleProduct(product.id)}
            sx={{ color: '#333', '&:hover': { color: '#00C853' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Description - видаляємо, щоб уникнути дублювання з Daily website sessions */}
      
      {/* Dynamic Sessions Slider */}
      {(callTrackingType === 'dynamic' || callTrackingType === 'combined') && (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2, mb: 0.5 }}>
            {t('calculator.dailyWebsiteSessions', 'Daily website sessions')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flex: 1, mr: 2 }}>
              <Slider
                value={typeof sessionsInput === 'number' ? sessionsInput : product.value} // Use input value for slider display
                min={product.min}
                max={10000} // Слайдер до 10000 для зручності
                onChange={(_, v) => {
                  setSessionsInput(v); // Update local state immediately for smooth UX
                }}
                onChangeCommitted={(_, v) => {
                  product.setValue(Number(v)); // Update context state only when user releases
                }}
                sx={{ 
                  color: '#00C853', 
                  '& .MuiSlider-thumb': {
                    width: 16,
                    height: 16,
                    backgroundColor: '#00C853',
                  }
                }}
              />
            </Box>
            <TextField
              value={sessionsInput} // TextField uses local string state  
              onChange={e => setSessionsInput(e.target.value)}
              onBlur={() => {
                // Дозволяємо вводити будь-які значення в інпуті, тольки мінімум
                let numericValue = parseInt(sessionsInput, 10);
                if (isNaN(numericValue) || numericValue < product.min) {
                  numericValue = product.min;
                }
                setSessionsInput(numericValue);
                product.setValue(numericValue);
              }}
              type="number"
              variant="outlined"
              size="small"
              sx={{ width: 90 }}
              inputProps={{ inputMode: 'numeric' }}
            />
          </Box>
        </>
      )}
      
      {/* Static Sources Slider */}
      {(callTrackingType === 'static' || callTrackingType === 'combined') && (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: callTrackingType === 'combined' ? 2 : 2, mb: 0.5 }}>
            {t('calculator.staticSourcesDescription', 'Static sources (1 source = 1 phone number)')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flex: 1, mr: 2 }}>
              <Slider
                value={typeof sourcesInput === 'number' ? sourcesInput : staticSources} // Use input value for slider display
                min={0}
                max={100} // Слайдер до 100 для зручності
                onChange={(_, v) => {
                  setSourcesInput(v); // Update local state immediately for smooth UX
                }}
                onChangeCommitted={(_, v) => {
                  setStaticSources(Number(v)); // Update context state only when user releases
                }}
                sx={{ 
                  color: '#00C853', 
                  '& .MuiSlider-thumb': {
                    width: 16,
                    height: 16,
                    backgroundColor: '#00C853',
                  }
                }}
              />
            </Box>
            <TextField
              value={sourcesInput} // TextField uses local string state
              onChange={e => setSourcesInput(e.target.value)}
              onBlur={() => {
                // Дозволяємо вводити будь-які значення в інпуті, тольки мінімум  
                let numericValue = parseInt(sourcesInput, 10);
                if (isNaN(numericValue) || numericValue < 0) {
                  numericValue = 0;
                }
                setSourcesInput(numericValue);
                setStaticSources(numericValue);
              }}
              type="number"
              variant="outlined"
              size="small"
              sx={{ width: 90 }}
              inputProps={{ inputMode: 'numeric' }}
            />
          </Box>
        </>
      )}
      
      {/* Phone Numbers Needed Info */}
      {phoneNumbersNeeded && phoneNumbersNeeded.total > 0 && (
        <Box sx={{ 
          mt: 2, 
          p: 1.5, 
          bgcolor: 'rgba(0, 200, 83, 0.05)', 
          borderRadius: 1, 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
            <InfoOutlinedIcon fontSize="small" sx={{ mr: 0.5, fontSize: 16 }} />
            {t('calculator.phoneNumbersRequired', 'Phone numbers required')}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#00C853' }}>
            {phoneNumbersNeeded?.total || 0}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(CallTracking);
